import { computed } from 'vue';
import { useRemoteConfigStore } from '../stores/remoteConfig.store';
import type { MaintenanceConfig, MaintenanceState } from '../types/maintenance.type';

/**
 * Gestiona el modo mantenimiento con valores tipados y predecibles desde Firebase Remote Config
 */
export function useMaintenance() {
  const remoteConfigStore = useRemoteConfigStore();
  const maintenanceConfig = remoteConfigStore.getRemoteConfigValue('feature_maintenance_mode');

  const config = computed<MaintenanceConfig>(() => maintenanceConfig.value);
  const isMaintenanceMode = computed<boolean>(() => config.value.isActive);
  const message = computed<string>(() => config.value.message || '');
  const estimatedTime = computed<string>(() => config.value.estimatedTime || '');
  const reason = computed<string>(() => config.value.reason || '');

  const hasMessage = computed<boolean>(() => message.value.length > 0);
  const hasEstimatedTime = computed<boolean>(() => estimatedTime.value.length > 0);
  const hasReason = computed<boolean>(() => reason.value.length > 0);

  const state = computed<MaintenanceState>(() => ({
    config: config.value,
    isActive: isMaintenanceMode.value,
    hasMessage: hasMessage.value,
    hasEstimatedTime: hasEstimatedTime.value,
    hasReason: hasReason.value,
  }));

  function getMessageWithFallback(defaultMessage: string = 'Estamos en mantenimiento'): string {
    return message.value || defaultMessage;
  }

  function getEstimatedTimeWithFallback(defaultTime: string = '2 horas'): string {
    return estimatedTime.value || defaultTime;
  }

  function shouldBlockAccess(): boolean {
    return isMaintenanceMode.value;
  }

  return {
    config,
    isMaintenanceMode,
    message,
    estimatedTime,
    reason,
    hasMessage,
    hasEstimatedTime,
    hasReason,
    state,
    getMessageWithFallback,
    getEstimatedTimeWithFallback,
    shouldBlockAccess,
  };
}

export type UseMaintenanceReturn = ReturnType<typeof useMaintenance>;
